/* ============================================================================
   twc-ui.js — TOKYO WIG CREATE v6.3 interface behaviour

   Ported from the inline <script> of previews/home-v6.3-release-candidate.html.

   Independent features, each a no-op when its markup is absent:
     1. setupUniqueThemeIds() de-duplicate Split's desktop/mobile navigation ids
     2. setupMenu()           mobile menu open/close + body scroll lock
     3. setupRangeDialog()    Interpretation Range dialog, three views + fallback
     4. setupProcessForm()    How It Works form strip navigation
     5. setupQuoteFab()       floating "Start a Free Quote" button
     6. setupFaqGroups()      one answer open at a time within a FAQ group
     7. setupDestinationPricing() explicit U.S. / outside-U.S. price display
     8. setupColorIndexTarget()  ?color=<handle> fallback for the color index

   This file must never touch the quote form. That is first-contact.js, which is
   loaded separately and is not to be modified (spec §1.2).

   Differences from the static source, and why:
     - Wrapped in an idempotence guard. A Shopify page can end up loading the
       same asset twice; without the guard every listener would be bound twice.
     - Body lock classes renamed `menu-open` / `dialog-open` ->
       `twc-menu-open` / `twc-dialog-open`. The theme and the XO Gallery app also
       lock the body, and a shared class name means one closing the other's lock
       (spec §8-C).
     - Every entry point null-checks its markup. The menu half exists for a later
       phase that may add sections/twc-header.liquid; while the theme's own header
       is in use it simply finds nothing and returns.
     - The range dialog reads data-front/-side/-back that Liquid now fills from
       image_picker / file_url values instead of hardcoded CDN URLs.
   ========================================================================== */

(function () {
  "use strict";

  /* Static review pages already own their menu/dialog behaviours. Loading this
     asset with data-twc-destination-only keeps the price selector on the exact
     production code path without binding those other behaviours twice. */
  var destinationOnly = Boolean(document.currentScript && document.currentScript.dataset.twcDestinationOnly === "true");
  if (destinationOnly) {
    if (window.__twcDestinationPricingLoaded) return;
    window.__twcDestinationPricingLoaded = true;
  } else {
    if (window.__twcUiLoaded) return;
    window.__twcUiLoaded = true;
  }

  /* Split renders the same navigation markup once for desktop and once inside
     the mobile drawer. Its menu-item and localization ids are therefore
     duplicated before any TWC section is rendered. Keep the desktop ids stable
     and suffix only duplicates inside the mobile drawer, including local
     references such as label[for] and aria-controls. */
  function setupUniqueThemeIds() {
    var mobileNavigation = document.getElementById("site-nav--mobile");
    if (!mobileNavigation) return;

    var referenceAttributes = [
      "for",
      "aria-controls",
      "aria-labelledby",
      "aria-describedby",
      "headers"
    ];

    Array.prototype.forEach.call(mobileNavigation.querySelectorAll("[id]"), function (element) {
      var originalId = element.id;
      if (!originalId || document.querySelectorAll('[id="' + originalId + '"]').length < 2) return;

      var replacementId = originalId + "-mobile";
      Array.prototype.forEach.call(mobileNavigation.querySelectorAll("*"), function (candidate) {
        referenceAttributes.forEach(function (attribute) {
          var value = candidate.getAttribute(attribute);
          if (!value) return;
          var tokens = value.split(/\s+/).map(function (token) {
            return token === originalId ? replacementId : token;
          });
          candidate.setAttribute(attribute, tokens.join(" "));
        });

        if (candidate.getAttribute("href") === "#" + originalId) {
          candidate.setAttribute("href", "#" + replacementId);
        }
      });

      element.id = replacementId;
    });
  }

  /* ═══ 2. MOBILE MENU ═══ */
  function setupMenu() {
    var menuButton = document.querySelector(".twc-page .menu-button");
    var mobileMenu = document.querySelector(".twc-page .mobile-menu");
    if (!menuButton || !mobileMenu) return;

    var label = menuButton.querySelector(".visually-hidden");

    function closeMenu() {
      menuButton.setAttribute("aria-expanded", "false");
      if (label) label.textContent = "Open menu";
      mobileMenu.hidden = true;
      document.body.classList.remove("twc-menu-open");
    }

    menuButton.addEventListener("click", function () {
      var opening = menuButton.getAttribute("aria-expanded") !== "true";
      menuButton.setAttribute("aria-expanded", String(opening));
      if (label) label.textContent = opening ? "Close menu" : "Open menu";
      mobileMenu.hidden = !opening;
      document.body.classList.toggle("twc-menu-open", opening);
    });

    mobileMenu.addEventListener("click", function (event) {
      if (event.target.closest("a")) closeMenu();
    });

    document.addEventListener("keydown", function (event) {
      if (event.key === "Escape" && !mobileMenu.hidden) closeMenu();
    });
  }

  /* ═══ 2. INTERPRETATION RANGE DIALOG ═══ */
  var RANGE_VIEWS = [
    { key: "front", label: "Front" },
    { key: "side", label: "Side" },
    { key: "back", label: "Back" }
  ];

  function setupRangeDialog() {
    var dialog = document.getElementById("rangeDialog");
    var cards = document.querySelectorAll(".range-card");
    if (!dialog || !cards.length || typeof dialog.showModal !== "function") return;

    var closeButton = dialog.querySelector(".range-dialog__close");
    var axisEl = document.getElementById("rangeDialogAxis");
    var titleEl = document.getElementById("rangeDialogTitle");
    var descriptionEl = document.getElementById("rangeDialogDescription");
    var visual = document.getElementById("rangeDialogVisual");
    var views = document.getElementById("rangeDialogViews");
    var single = document.getElementById("rangeDialogSingle");
    var singleImage = single ? single.querySelector("img") : null;
    var activeCard = null;

    /* Clearing src="" would re-request the page itself, so a hidden frame drops
       the attribute instead of pointing at an empty URL. */
    function clearImage(image) {
      if (!image) return;
      image.removeAttribute("src");
      image.alt = "";
    }

    /* Three views when the card carries data-front/-side/-back, otherwise the
       single card photo. Never an empty frame: a view without a URL is removed. */
    function fillVisual(card) {
      var title = card.dataset.title || "";
      var hasThreeViews = Boolean((card.dataset.front || "").trim());

      /* Some works have no straight-on front cut (side / side / back, say).
         data-labels="Side|Side|Back" renames the frames per card so a side view
         is never captioned "Front". */
      var customLabels = (card.dataset.labels || "").split("|").map(function (v) {
        return v.trim();
      });

      RANGE_VIEWS.forEach(function (view, i) {
        var item = views ? views.querySelector('[data-view="' + view.key + '"]') : null;
        if (!item) return;
        var image = item.querySelector("img");
        var label = customLabels[i] || view.label;
        var caption = item.querySelector(".range-view__caption");
        if (caption) caption.textContent = label;

        var source = hasThreeViews ? (card.dataset[view.key] || "").trim() : "";
        item.hidden = !source;
        if (source) {
          image.src = source;
          image.alt = label + " view of the finished wig — " + title;
        } else {
          clearImage(image);
        }
      });

      var cardImage = card.querySelector(".media-slot");
      var cardSource = cardImage ? (cardImage.getAttribute("src") || "").trim() : "";
      if (!hasThreeViews && cardSource && singleImage) {
        singleImage.src = cardSource;
        singleImage.alt = cardImage.getAttribute("alt") || "";
      } else {
        clearImage(singleImage);
      }

      if (views) views.hidden = !hasThreeViews;
      if (single) single.hidden = hasThreeViews || !cardSource;
      if (visual) visual.hidden = !hasThreeViews && !cardSource;
      if (hasThreeViews && views) views.scrollLeft = 0;
    }

    function openRange(card) {
      activeCard = card;
      if (axisEl) axisEl.textContent = card.dataset.axis || "";
      if (titleEl) titleEl.textContent = card.dataset.title || "";
      if (descriptionEl) descriptionEl.textContent = card.dataset.description || "";
      fillVisual(card);
      dialog.showModal();
      document.body.classList.add("twc-dialog-open");
      /* Measured after showModal, when the row finally has a layout: the view
         strip only becomes a keyboard tab stop at widths where it really scrolls. */
      if (views) {
        if (views.scrollWidth > views.clientWidth) views.tabIndex = 0;
        else views.removeAttribute("tabindex");
      }
      if (closeButton) closeButton.focus();
    }

    function closeRange() { dialog.close(); }

    Array.prototype.forEach.call(cards, function (card) {
      card.addEventListener("click", function () { openRange(card); });
      card.addEventListener("keydown", function (event) {
        if (event.key === "Enter" || event.key === " ") {
          event.preventDefault();
          openRange(card);
        }
      });
    });

    /* Cleanup lives on the close event so Escape — which closes the dialog
       natively — restores page scroll and focus exactly like the close button. */
    dialog.addEventListener("close", function () {
      document.body.classList.remove("twc-dialog-open");
      if (activeCard) activeCard.focus();
      activeCard = null;
    });

    if (closeButton) closeButton.addEventListener("click", closeRange);

    dialog.addEventListener("click", function (event) {
      var rect = dialog.getBoundingClientRect();
      var inside =
        event.clientX >= rect.left && event.clientX <= rect.right &&
        event.clientY >= rect.top && event.clientY <= rect.bottom;
      if (!inside) closeRange();
    });
  }

  /* ═══ 3B. HOME — PRE-PRODUCTION FORM DIALOG ═══ */
  function setupPreProductionFormDialog() {
    var dialog = document.getElementById("preProductionFormDialog");
    var triggers = document.querySelectorAll("[data-open-preproduction-form]");
    if (!dialog || !triggers.length || typeof dialog.showModal !== "function") return;

    var closeButton = dialog.querySelector(".preproduction-dialog__close");
    var activeTrigger = null;

    Array.prototype.forEach.call(triggers, function (trigger) {
      trigger.addEventListener("click", function (event) {
        event.preventDefault();
        activeTrigger = trigger;
        dialog.showModal();
        document.body.classList.add("twc-dialog-open");
        if (closeButton) closeButton.focus();
        /* A closed dialog has no measurable width or slide height. Re-run the
           form viewer's resize path after it enters the top layer. */
        window.requestAnimationFrame(function () {
          window.dispatchEvent(new Event("resize"));
        });
      });
    });

    if (closeButton) closeButton.addEventListener("click", function () { dialog.close(); });
    dialog.addEventListener("click", function (event) {
      if (event.target === dialog) dialog.close();
    });
    dialog.addEventListener("close", function () {
      document.body.classList.remove("twc-dialog-open");
      if (activeTrigger) activeTrigger.focus();
      activeTrigger = null;
    });
  }

  /* ═══ 3C. HOW IT WORKS — PRE-PRODUCTION FORM STRIP ═══
     The form is readable as a horizontal scroll-snap row without JavaScript.
     This layer only keeps the arrows, progress and live counter in sync with
     native touch scrolling and keyboard navigation. */
  function setupProcessForm() {
    var root = document.querySelector("[data-process-form]");
    if (!root) return;

    var track = root.querySelector("[data-process-form-track]");
    var slides = Array.prototype.slice.call(root.querySelectorAll(".process-form__slide"));
    var previous = root.querySelector("[data-process-form-prev]");
    var next = root.querySelector("[data-process-form-next]");
    var counter = root.querySelector("[data-process-form-counter]");
    var status = root.querySelector("[data-process-form-status]");
    var progress = root.querySelector("[data-process-form-progress]");
    if (!track || !slides.length || !previous || !next) return;

    var index = 0;
    function fitTrack(nextIndex) {
      var styles = window.getComputedStyle(track);
      var padding = parseFloat(styles.paddingTop) + parseFloat(styles.paddingBottom);
      track.style.height = Math.ceil(slides[nextIndex].offsetHeight + padding) + "px";
      track.classList.add("is-height-managed");
    }

    /* syncUi never touches track height. Resizing the scroll container while a
       touch fling is in flight cancels the momentum on iOS, so height changes are
       deferred to scheduleFit() below and only land once the gesture has settled. */
    function syncUi(nextIndex) {
      index = Math.max(0, Math.min(slides.length - 1, nextIndex));
      previous.disabled = index === 0;
      next.disabled = index === slides.length - 1;
      if (counter) counter.textContent = String(index + 1).padStart(2, "0") + " / " + String(slides.length).padStart(2, "0");
      if (status) status.textContent = "Section " + (index + 1) + " of " + slides.length;
      if (progress) progress.style.width = ((index + 1) / slides.length * 100) + "%";
    }

    /* The rail sits inside the dialog on Home and directly on the page on How It
       Works, so the vertical scroller is whichever ancestor actually scrolls. */
    function verticalScroller(el) {
      var node = el.parentElement;
      while (node && node !== document.body) {
        var s = window.getComputedStyle(node);
        if (/(auto|scroll)/.test(s.overflowY) && node.scrollHeight > node.clientHeight) return node;
        node = node.parentElement;
      }
      return null; /* window */
    }

    /* Owner requirement: every new section must be read from its top, because the
       reader may have scrolled deep inside the previous, taller section. This runs
       only from the settle path — moving vertically while the horizontal scroll is
       still in flight cancels the iOS fling and fights the snap. */
    function revealTop(nextIndex) {
      var slide = slides[nextIndex]; if (!slide) return;
      var reduce = window.matchMedia && window.matchMedia("(prefers-reduced-motion: reduce)").matches;
      var behavior = reduce ? "auto" : "smooth";
      var margin = 12;
      var scroller = verticalScroller(track);
      var slideTop = slide.getBoundingClientRect().top;
      if (scroller) {
        var scTop = scroller.getBoundingClientRect().top;
        var bar = scroller.querySelector(".preproduction-dialog__bar");
        var barH = bar ? bar.getBoundingClientRect().height : 0;
        var delta = slideTop - scTop - barH - margin;
        if (Math.abs(delta) > 2) scroller.scrollBy({ top: delta, behavior: behavior });
      } else {
        var header = document.getElementById("site-header");
        var headH = header && window.getComputedStyle(header.parentElement || header).position === "sticky" ? header.getBoundingClientRect().height : 0;
        var deltaW = slideTop - headH - margin;
        if (Math.abs(deltaW) > 2) window.scrollBy({ top: deltaW, behavior: behavior });
      }
    }

    /* fitTrack animates the track height (240ms in twc-system.css), so at settle
       time the dialog has not grown yet and would still measure as non-scrollable.
       Measure once that transition ends, or after a fallback when it is disabled. */
    var revealTimer = 0;
    var revealTarget = -1;
    function runReveal() {
      if (revealTarget < 0) return;
      var target = revealTarget;
      revealTarget = -1;
      if (revealTimer) { window.clearTimeout(revealTimer); revealTimer = 0; }
      track.removeEventListener("transitionend", onTrackTransitionEnd);
      revealTop(target);
    }
    function onTrackTransitionEnd(event) {
      if (event.target === track && event.propertyName === "height") runReveal();
    }
    function revealAfterFit(target) {
      if (revealTimer) window.clearTimeout(revealTimer);
      revealTarget = target;
      track.addEventListener("transitionend", onTrackTransitionEnd);
      revealTimer = window.setTimeout(runReveal, 320);
    }

    var lastRevealed = 0;
    function settle() {
      fitTrack(index);
      if (index !== lastRevealed) { lastRevealed = index; revealAfterFit(index); }
    }

    var settleTimer = 0;
    function scheduleFit() {
      if (settleTimer) window.clearTimeout(settleTimer);
      settleTimer = window.setTimeout(function () { settleTimer = 0; settle(); }, 160);
    }
    if ("onscrollend" in window) {
      track.addEventListener("scrollend", function () {
        if (settleTimer) { window.clearTimeout(settleTimer); settleTimer = 0; }
        settle();
      }, { passive: true });
    }

    function goTo(nextIndex) {
      var target = Math.max(0, Math.min(slides.length - 1, nextIndex));
      var reduce = window.matchMedia && window.matchMedia("(prefers-reduced-motion: reduce)").matches;
      track.scrollTo({
        left: slides[target].offsetLeft - (track.clientWidth - slides[target].offsetWidth) / 2,
        behavior: reduce ? "auto" : "smooth"
      });
      syncUi(target);
      scheduleFit();
    }

    previous.addEventListener("click", function () { goTo(index - 1); });
    next.addEventListener("click", function () { goTo(index + 1); });
    track.addEventListener("keydown", function (event) {
      if (event.key !== "ArrowLeft" && event.key !== "ArrowRight") return;
      event.preventDefault();
      goTo(index + (event.key === "ArrowRight" ? 1 : -1));
    });
    track.addEventListener("scroll", function () {
      var center = track.scrollLeft + track.clientWidth / 2;
      var nearest = slides.reduce(function (best, slide, slideIndex) {
        var slideCenter = slide.offsetLeft + slide.offsetWidth / 2;
        var distance = Math.abs(center - slideCenter);
        return distance < best.distance ? { distance: distance, index: slideIndex } : best;
      }, { distance: Infinity, index: 0 });
      if (nearest.index !== index) syncUi(nearest.index);
      scheduleFit();
    }, { passive: true });
    window.addEventListener("resize", function () { fitTrack(index); });
    if ("ResizeObserver" in window) {
      var processFormResizeObserver = new ResizeObserver(function () { fitTrack(index); });
      slides.forEach(function (slide) { processFormResizeObserver.observe(slide); });
    }
    syncUi(0);
    fitTrack(0);
  }

  /* ═══ 4. QUOTE FAB ═══ */
  function setupQuoteFab() {
    var fab = document.getElementById("quoteFab");
    if (!fab || !("IntersectionObserver" in window)) return;

    var heroSection = document.querySelector(".twc-page--home.hero, .twc-page .hero");
    var quoteSection = document.getElementById("quote");

    /* On a page without #quote (the eight individual pages, where the FAB links
       to /#quoteForm) there is nothing to hide behind, so the button simply shows
       once the first screen has been passed — spec §6.4. */
    if (!heroSection && !quoteSection) {
      fab.hidden = false;
      var onSimpleScroll = function () {
        fab.dataset.visible = String(window.scrollY >= window.innerHeight);
      };
      window.addEventListener("scroll", onSimpleScroll, { passive: true });
      onSimpleScroll();
      return;
    }

    fab.hidden = false;
    var heroVisible = Boolean(heroSection);
    var quoteVisible = false;
    var pastFirstScreen = false;

    var syncFab = function () {
      fab.dataset.visible = String(pastFirstScreen && !heroVisible && !quoteVisible);
    };

    var sectionObserver = new IntersectionObserver(function (entries) {
      entries.forEach(function (entry) {
        if (entry.target === heroSection) heroVisible = entry.isIntersecting;
        if (entry.target === quoteSection) quoteVisible = entry.isIntersecting;
      });
      syncFab();
    });
    if (heroSection) sectionObserver.observe(heroSection);
    if (quoteSection) sectionObserver.observe(quoteSection);

    var onFabScroll = function () {
      pastFirstScreen = window.scrollY >= window.innerHeight;
      syncFab();
    };
    window.addEventListener("scroll", onFabScroll, { passive: true });
    onFabScroll();
  }

  /* ═══ 5. FAQ GROUPS ═══
     Progressive enhancement over <details>/<summary>, nothing more. The page is
     already complete without this: every answer opens, closes, takes focus and
     announces its state on its own. All this adds is that opening one answer
     closes the others in the same group, so a group stays one screen long.

     Scoped per .faq-list, so the five groups do not close each other — a visitor
     comparing a payment answer with a delivery answer keeps both.

     `toggle` does not bubble, hence the capture phase on the list. */
  function setupFaqGroups() {
    var lists = document.querySelectorAll(".twc-page .faq-list");
    if (!lists.length) return;

    Array.prototype.forEach.call(lists, function (list) {
      list.addEventListener(
        "toggle",
        function (event) {
          var opened = event.target;
          if (!opened.open || opened.tagName !== "DETAILS") return;
          var siblings = list.querySelectorAll("details[open]");
          Array.prototype.forEach.call(siblings, function (item) {
            if (item !== opened) item.open = false;
          });
        },
        true
      );
    });
  }

  /* ═══ 6. DESTINATION PRICING ═══
     The wig's published price is identical in both views. U.S. adds fixed 20%
     destination price component to the quote; outside the U.S. shows the published price
     and makes the recipient-paid import rule explicit. The selection persists
     only for the current browser session so Home and Pricing do not contradict
     each other while a visitor compares them. */
  function setupDestinationPricing() {
    var roots = document.querySelectorAll("[data-destination-pricing]");
    if (!roots.length) return;

    var storageKey = "twc-destination-market";
    var selectedMarket = "us";
    try {
      var storedMarket = window.sessionStorage.getItem(storageKey);
      if (storedMarket === "us" || storedMarket === "outside-us") selectedMarket = storedMarket;
    } catch {
      /* Storage can be unavailable in private browsing. The control still works. */
    }

    function usd(value) {
      return "$" + Math.round(value).toLocaleString("en-US");
    }

    function applyMarket(root, market) {
      root.dataset.destinationMarket = market;

      Array.prototype.forEach.call(root.querySelectorAll('.destination-option input[type="radio"]'), function (radio) {
        radio.checked = radio.value === market;
      });

      Array.prototype.forEach.call(root.querySelectorAll("[data-destination-price]"), function (cell) {
        var basePrice = Number(cell.dataset.basePrice);
        if (!Number.isFinite(basePrice)) return;

        var coverage = Math.round(basePrice * 0.2);
        var total = basePrice + coverage;
        var amount = cell.querySelector("[data-price-amount]");
        var breakdown = cell.querySelector("[data-price-breakdown]");

        if (market === "us") {
          if (amount) amount.textContent = usd(total);
          if (breakdown) {
            breakdown.hidden = false;
            breakdown.textContent = "Base " + usd(basePrice) + " + " + usd(coverage);
          }
          cell.setAttribute("aria-label", usd(total) + " U.S. quote total. Base " + usd(basePrice) + " plus " + usd(coverage) + " U.S. destination price component.");
        } else {
          if (amount) amount.textContent = usd(basePrice);
          if (breakdown) {
            breakdown.textContent = "";
            breakdown.hidden = true;
          }
          cell.setAttribute("aria-label", usd(basePrice) + " published order price. Local import charges are paid by the recipient if charged.");
        }
      });

      var label = root.querySelector("[data-price-table-label]");
      if (label) label.textContent = market === "us" ? "U.S. quote totals" : "9 published order prices";

      var corner = root.querySelector("[data-price-corner]");
      if (corner) corner.textContent = market === "us" ? "U.S. totals" : "Published prices";

      var caption = root.querySelector("[data-price-caption]");
      if (caption) {
        caption.textContent = market === "us"
          ? "U.S. quote total by complexity and production schedule. Each total is the published order price plus a fixed 20% U.S. destination price component."
          : "Published order price by complexity and production schedule. Local import charges are paid separately by the recipient if charged.";
      }

      var table = root.querySelector("[data-price-table]");
      if (table) {
        table.setAttribute("aria-label", market === "us"
          ? "U.S. quote totals by complexity and production schedule"
          : "Published order prices by complexity and production schedule");
      }

      Array.prototype.forEach.call(root.querySelectorAll("[data-destination-copy]"), function (element) {
        var nextText = market === "us" ? element.dataset.usText : element.dataset.outsideUsText;
        if (typeof nextText === "string") element.textContent = nextText;
      });

      Array.prototype.forEach.call(root.querySelectorAll("[data-destination-visible]"), function (element) {
        element.hidden = element.dataset.destinationVisible !== market;
      });
    }

    function syncAll(market) {
      selectedMarket = market;
      Array.prototype.forEach.call(roots, function (root) { applyMarket(root, market); });
      try { window.sessionStorage.setItem(storageKey, market); } catch { /* no-op */ }
    }

    Array.prototype.forEach.call(roots, function (root) {
      root.addEventListener("change", function (event) {
        var radio = event.target.closest('.destination-option input[type="radio"]');
        if (!radio || (radio.value !== "us" && radio.value !== "outside-us")) return;
        syncAll(radio.value);
      });
    });

    syncAll(selectedMarket);
  }

  /* Color index arrivals.

     The archived $0.00 color products redirect to /pages/color-gallery#<handle>
     and the fragment does the work on its own. This is the insurance: if a
     redirect target ever has to drop its fragment, ?color=<handle> reaches the
     same row. Both paths also mark the row for one moment, because landing in a
     list of 173 near-identical lines with no indication of which one was asked
     for is the same as landing nowhere.

     The handle is only ever used with getElementById, never written into the
     DOM, so a crafted ?color= value cannot inject anything. */
  function setupColorIndexTarget() {
    var index = document.querySelector(".color-index__families");
    if (!index) return;

    var handle = "";
    try {
      handle = new URLSearchParams(window.location.search).get("color") || "";
    } catch {
      handle = "";
    }
    if (!handle && window.location.hash) handle = window.location.hash.slice(1);
    handle = handle.trim().toLowerCase();
    if (!/^[a-z0-9-]{1,120}$/.test(handle)) return;

    var anchor = document.getElementById(handle);
    if (!anchor || !index.contains(anchor)) return;

    var item = anchor.closest(".color-index__item");
    if (!item) return;

    /* The browser has already handled a fragment by this point; scrolling again
       for one would fight it. A query string it has never scrolled for. */
    if (!window.location.hash) {
      try {
        item.scrollIntoView({ block: "center", behavior: "auto" });
      } catch {
        item.scrollIntoView();
      }
    }

    item.classList.add("is-targeted");
    window.setTimeout(function () { item.classList.remove("is-targeted"); }, 2600);
  }

  function init() {
    if (destinationOnly) {
      setupDestinationPricing();
      return;
    }
    setupUniqueThemeIds();
    setupMenu();
    setupRangeDialog();
    setupPreProductionFormDialog();
    setupProcessForm();
    setupQuoteFab();
    setupFaqGroups();
    setupDestinationPricing();
    setupColorIndexTarget();
  }

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", init);
  } else {
    init();
  }
})();
